import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { DoorClosed, Users, Pencil, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { PossessionVoiceDialog, PossessionStatsDialog } from './PossessionDialogs';
import IntelLock from '../rera/IntelLock';

const API = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, tone = '', testid }) => (
  <div className={`rounded-lg border p-3 ${tone}`} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold mt-0.5">{value ?? '—'}</p>
  </div>
);

// The pain point: purchasers who have NOT taken possession — discover & collate first, collectively.
export default function PossessionPicture({ projectId, onOpenAuth }) {
  const { user, isAdmin } = useAuth();
  const [s, setS] = useState(null);
  const [mine, setMine] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/api/possession/${projectId}/summary`).then((r) => setS(r.data)).catch(() => {});
    if (user) axios.get(`${API}/api/possession/${projectId}/me`).then((r) => setMine(r.data)).catch(() => {});
  }, [projectId, user]);
  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  if (!s) return null;
  const st = s.stats || {};
  const pending = s.pending_estimate;
  const voices = s.reported_not_taken;
  const strength = pending ? Math.min(100, Math.round((voices / pending) * 100)) : 0;
  const maxIssue = Math.max(1, ...s.issues.map((i) => i.count));
  const ranked = [...s.issues].sort((a, b) => b.count - a.count);

  const addVoice = () => {
    if (!user) { toast.info('Sign in to be counted'); return onOpenAuth?.(); }
    setVoiceOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="possession-picture">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Flats sold" value={st.units_sold} testid="stat-units-sold" />
        <Stat label="Possession taken" value={st.possession_taken} testid="stat-possession-taken" />
        <Stat label="Yet to take possession" value={pending != null ? `~${pending}` : null}
          tone="border-rose-300 bg-rose-50/60 dark:bg-rose-950/20" testid="stat-pending" />
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-muted-foreground">
        <span>
          {st.basis_note || 'Figures to be derived from RERA sales and corpus tendered.'}
          {st.wings_pending_oc && <> · <span className="text-amber-700 font-medium">OC pending: {st.wings_pending_oc}</span></>}
        </span>
        {isAdmin && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setStatsOpen(true)} data-testid="edit-possession-stats-btn">
            <Pencil className="h-3 w-3 mr-1" />Edit figures
          </Button>
        )}
      </div>

      <Card className="border-rose-300" data-testid="not-in-possession-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-start gap-3">
            <DoorClosed className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-sm">Sold, but not yet home</h4>
              <p className="text-xs text-muted-foreground mt-1">
                {pending ? `Around ${pending} purchasers` : 'Many purchasers'} have not taken possession — because of quality,
                incomplete works, OC still pending for their wing, disputed demands, or conditions on society admission.
                First, let's find out exactly what is holding each of us back. One complaint is easy to ignore; a documented
                pattern across hundreds of flats is not.
              </p>
            </div>
          </div>

          <div className="rounded-md bg-muted/40 p-3" data-testid="collective-strength">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="flex items-center gap-1 font-medium"><Users className="h-3.5 w-3.5" />Strength of the collective</span>
              <span className="font-semibold" data-testid="collective-count">{voices}{pending ? ` of ~${pending}` : ''} counted</span>
            </div>
            <Progress value={strength} className="h-2" />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Every voice added makes the pattern harder for the developer to dismiss. Representations come once the picture is complete.
            </p>
          </div>

          {s.tier === 'anonymous' ? (
            <IntelLock need="registered" compact testid="possession-lock" title="Sign in free to see the issue register and add your voice" />
          ) : (<>
          <div className="space-y-1.5" data-testid="issue-register">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Issue register — what purchasers report</p>
            {ranked.map((i) => (
              <div key={i.code} className="flex items-center gap-2 text-xs" data-testid={`issue-row-${i.code}`}>
                <span className="w-56 shrink-0 truncate">{i.label}</span>
                <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                  <div className="h-full bg-rose-400" style={{ width: `${(i.count / maxIssue) * 100}%` }} />
                </div>
                <span className="w-6 text-right font-medium">{i.count}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={addVoice} data-testid="add-possession-voice-btn">
              <Megaphone className="h-4 w-4 mr-1" />{mine?.status ? 'Update my status' : 'Add your voice'}
            </Button>
            {mine?.status && (
              <Badge variant="outline" className="text-[10px]" data-testid="my-possession-status">
                You reported: {mine.status === 'taken' ? 'possession taken' : 'not yet taken'}
              </Badge>
            )}
          </div>
          </>)}
        </CardContent>
      </Card>

      {voiceOpen && (
        <PossessionVoiceDialog open={voiceOpen} onOpenChange={setVoiceOpen} projectId={projectId}
          issues={s.issues} initial={mine} onSaved={(d) => { setS(d); load(); }} />
      )}
      {statsOpen && (
        <PossessionStatsDialog open={statsOpen} onOpenChange={setStatsOpen} projectId={projectId} stats={st} onSaved={load} />
      )}
    </div>
  );
}
